import cn from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "../grid";
import { useLatestRef } from "../common/hooks";
import { Track } from "./components/track";
import { ControlBox } from "./components/control-box";
import {
  DEFAULT_GAP,
  CONTROL_WIDTHS,
  VIEW_MODES,
  ACTIVE_SLIDE_ALIGNMENTS,
  DEFAULT_OFFSET,
  MIN_SLIDE_INDEX,
  ACTIVE_SLIDE_SCALE,
} from "./constants";
import { CarouselProps } from "./types";
import {
  getControlsVisibility,
  isSlideIndexValid,
  getTrackOffset,
  mapChildrenToElements,
  getMaxSlideIndex,
  normalizeSlideIndex,
  getExtraSlidesMultiplier,
} from "./utils";
import { useViewMode } from "./hooks";
import { useTransitionDuration } from "./hooks/use-transition-duration";
import { useSlidesWidth } from "./hooks/use-slides-width";
import styles from "./styles.module.scss";

export const Carousel = ({
  children,
  className,
  onChangeSlide,
  gap = DEFAULT_GAP,
  viewMode = VIEW_MODES.MULTIPLE,
  activeSlide: activeSlideFromProps,
  activeSlideAlignment = ACTIVE_SLIDE_ALIGNMENTS.LEFT,
  loopMode,
  dataTestId = "carousel",
}: CarouselProps) => {
  const trackRef = useRef<HTMLUListElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);

  const isDesktopXL = useMediaQuery("xl");

  const isControlled =
    typeof onChangeSlide === "function" && activeSlideFromProps !== undefined;

  const slides = mapChildrenToElements(children);
  const slidesCount = slides.length;

  const { isAllSlidesMeasured, setSlideRef, slideRefs, slidesWidthRef } =
    useSlidesWidth(slidesCount);

  const [trackOffset, setTrackOffset] = useState(DEFAULT_OFFSET);

  const [isLeftControlHidden, setIsLeftControlHidden] = useState(true);

  const [isRightControlHidden, setIsRightControlHidden] = useState(true);

  const activeSlideIndexRef = useRef(activeSlideFromProps ?? MIN_SLIDE_INDEX);

  const { applyTransition, disableTransition, transitionDuration } =
    useTransitionDuration();

  const [shouldRenderExtraSlides, setShouldRenderExtraSlides] = useState(false);

  const [extraSlidesMultiplier, setExtraSlidesMultiplier] = useState(1);

  const { isSingleViewMode, isMultipleViewMode } = useViewMode(viewMode);

  const isCenterMode =
    activeSlideAlignment === ACTIVE_SLIDE_ALIGNMENTS.CENTER &&
    isMultipleViewMode;

  const controlsWidth = isDesktopXL
    ? CONTROL_WIDTHS.large
    : CONTROL_WIDTHS.small;

  const gapOffset = isSingleViewMode ? DEFAULT_OFFSET : controlsWidth + gap;

  useEffect(() => {
    if (!(isAllSlidesMeasured && loopMode)) {
      return;
    }

    disableTransition();
    const slidesWidth = slidesWidthRef.current;

    const slidesToMeasure =
      slidesWidth.length > 1 && shouldRenderExtraSlides
        ? slidesWidth.concat(slidesWidth.slice(0, activeSlideIndexRef.current))
        : [];

    const additionalCenterWidth =
      activeSlideAlignment === ACTIVE_SLIDE_ALIGNMENTS.CENTER
        ? (carouselRef.current?.getBoundingClientRect().width ?? 0) / 2
        : 0;

    const offset =
      slidesToMeasure.reduce(
        (totalWidth, slideWidth) => totalWidth + slideWidth + gapOffset,
        0
      ) - additionalCenterWidth;

    setTrackOffset(offset);
  }, [
    isAllSlidesMeasured,
    loopMode,
    disableTransition,
    gapOffset,
    shouldRenderExtraSlides,
    activeSlideAlignment,
    slidesWidthRef,
  ]);

  useEffect(() => {
    if (!(isAllSlidesMeasured && carouselRef.current)) {
      return;
    }

    const newExtraSlidesMultiplier = getExtraSlidesMultiplier({
      carouselWidth: carouselRef.current.getBoundingClientRect().width,
      slidesWidth: slidesWidthRef.current,
      gap,
    });

    setExtraSlidesMultiplier(newExtraSlidesMultiplier);
  }, [gap, isAllSlidesMeasured, slidesWidthRef]);

  const maxSlideIndex = getMaxSlideIndex(slidesCount);

  const updateTrackOffset = useCallback(() => {
    const newTrackOffset = getTrackOffset({
      carouselElement: carouselRef.current,
      trackElement: trackRef.current,
      slides: slideRefs.current,
      activeSlideIndex: activeSlideIndexRef.current,
      viewMode,
      gap,
      gapOffset,
      loopMode,
      slidesWidth: slidesWidthRef.current,
      shouldRenderExtraSlides,
      activeSlideAlignment,
      extraSlidesMultiplier,
    });

    setTrackOffset(newTrackOffset);
  }, [
    slideRefs,
    viewMode,
    gap,
    gapOffset,
    loopMode,
    slidesWidthRef,
    shouldRenderExtraSlides,
    activeSlideAlignment,
    extraSlidesMultiplier,
  ]);

  useEffect(() => {
    updateTrackOffset();
  }, [updateTrackOffset]);

  const onChangeSlideRef = useLatestRef(onChangeSlide);
  const handleChangeSlide = useCallback(
    (newSlideIndex: number) => {
      onChangeSlideRef.current?.(
        normalizeSlideIndex({ slideIndex: newSlideIndex, slidesCount })
      );
      if (!isControlled) {
        activeSlideIndexRef.current = newSlideIndex;
        updateTrackOffset();
      }
    },
    [isControlled, onChangeSlideRef, slidesCount, updateTrackOffset]
  );

  useEffect(() => {
    const slideIndexValid = isSlideIndexValid(activeSlideFromProps, {
      maxSlideIndex,
    });

    if (slideIndexValid) {
      applyTransition();
      activeSlideIndexRef.current = activeSlideFromProps;
      updateTrackOffset();
    }
  }, [maxSlideIndex, activeSlideFromProps, updateTrackOffset, applyTransition]);

  const checkControlsAvailability = useCallback(() => {
    const [isLeftControlVisible, isRightControlVisible] = getControlsVisibility(
      {
        activeSlideIndex: activeSlideIndexRef.current,
        isMultipleViewMode,
        maxSlideIndex,
        trackOffset,
        carouselWidth: carouselRef.current?.getBoundingClientRect().width ?? 0,
        trackWidth: trackRef.current?.getBoundingClientRect().width ?? 0,
        loopMode,
        shouldRenderExtraSlides,
        activeSlideAlignment,
        activeSlideWidth: slidesWidthRef.current[activeSlideIndexRef.current],
      }
    );

    setIsLeftControlHidden(!isLeftControlVisible);
    setIsRightControlHidden(!isRightControlVisible);
  }, [
    activeSlideAlignment,
    isMultipleViewMode,
    loopMode,
    maxSlideIndex,
    shouldRenderExtraSlides,
    slidesWidthRef,
    trackOffset,
  ]);

  useEffect(() => {
    const carouselElement = carouselRef.current;

    if (carouselElement) {
      const resizeObserver = new ResizeObserver(([entry]) => {
        checkControlsAvailability();
        updateTrackOffset();
        const shouldRenderExtraSlides =
          loopMode === true &&
          slidesCount > 1 &&
          (isSingleViewMode ||
            activeSlideAlignment === ACTIVE_SLIDE_ALIGNMENTS.CENTER ||
            (activeSlideAlignment === ACTIVE_SLIDE_ALIGNMENTS.LEFT &&
              slidesWidthRef.current.reduce(
                (totalWidth, slideWidth) => totalWidth + slideWidth + gap,
                0
              ) > (carouselRef.current?.getBoundingClientRect().width ?? 0)));

        setShouldRenderExtraSlides(shouldRenderExtraSlides);

        setExtraSlidesMultiplier(
          getExtraSlidesMultiplier({
            carouselWidth: entry.contentRect.width,
            slidesWidth: slidesWidthRef.current,
            gap,
          })
        );
      });

      resizeObserver.observe(carouselElement);

      return () => resizeObserver.unobserve(carouselElement);
    }
  }, [
    checkControlsAvailability,
    gap,
    loopMode,
    slidesCount,
    updateTrackOffset,
    isSingleViewMode,
    isMultipleViewMode,
    activeSlideAlignment,
    slidesWidthRef,
  ]);

  const handleLeftControlClick = () => {
    applyTransition();
    handleChangeSlide(activeSlideIndexRef.current - 1);
  };

  const handleRightControlClick = () => {
    applyTransition();
    handleChangeSlide(activeSlideIndexRef.current + 1);
  };

  const handleTrackTransitionEnd = () => {
    disableTransition();

    if (!loopMode) {
      return;
    }

    const activeSlideIndex = activeSlideIndexRef.current;
    const isSlideIndexOverCount =
      activeSlideIndex > maxSlideIndex || activeSlideIndex < 0;

    if (isSlideIndexOverCount) {
      const normalizedSlideIndex = normalizeSlideIndex({
        slideIndex: activeSlideIndex,
        slidesCount,
      });

      activeSlideIndexRef.current = normalizedSlideIndex;
      updateTrackOffset();
    }
  };

  const arrowLeftPosition =
    (carouselRef.current?.getBoundingClientRect().width ?? 0) / 2 -
    (slidesWidthRef.current[activeSlideIndexRef.current] * ACTIVE_SLIDE_SCALE) /
      2;

  return (
    <div
      className={cn(
        styles.container,
        { [styles["single-view-mode"]]: isSingleViewMode },
        className
      )}
      data-test-id={dataTestId}
    >
      <div
        ref={carouselRef}
        className={styles.carousel}
        data-test-id={`${dataTestId}-track-container`}
      >
        <Track
          activeSlideIndex={activeSlideIndexRef.current}
          isCenterMode={isCenterMode}
          gap={isSingleViewMode ? 0 : gap}
          ref={trackRef}
          slides={slides}
          viewMode={viewMode}
          offset={trackOffset * -1}
          transitionDuration={transitionDuration}
          shouldRenderExtraSlides={shouldRenderExtraSlides}
          extraSlidesMultiplier={extraSlidesMultiplier}
          setSlideRef={setSlideRef}
          onTransitionEnd={handleTrackTransitionEnd}
          dataTestId={`${dataTestId}-track`}
        />
      </div>

      <ControlBox
        position="left"
        isSingleViewMode={isSingleViewMode}
        isHiddenControl={isLeftControlHidden}
        isCenterMode={isCenterMode}
        dataTestId={dataTestId}
        onClick={handleLeftControlClick}
        offsetLeft={arrowLeftPosition}
      />

      <ControlBox
        position="right"
        isSingleViewMode={isSingleViewMode}
        isHiddenControl={isRightControlHidden}
        isCenterMode={isCenterMode}
        dataTestId={dataTestId}
        onClick={handleRightControlClick}
        offsetLeft={arrowLeftPosition}
      />
    </div>
  );
};
